class KeyboardShortcuts {
  constructor(eventHub) {
    this.eventHub = eventHub;
    // bind if you attach as a raw listener: window.addEventListener('keydown', this.handleKeyDown, true)
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  cleanup() {
    this.eventHub = null;
  }

  handleKeyDown(event) {
    // don't double-handle
    if (event.defaultPrevented) return;

    const key = event.key;
    const code = event.code;

    const active = document.activeElement;
    const isSearchFocused = active && active.id === "searchResultsText";

    // Hard block most shortcuts while typing in a text field.
    // (We’ll explicitly allow Enter-for-confirm later.)
    const typing = this.isTypingInTextField(event);

    // ---------- SPACE -> open global search ----------
    if (this.isSpace(code, key)) {
      if (!this.hasModifier(event) && !typing) {
        this._kill(event);
        this.eventHub.emit("globalSearchOpen");
        return;
      }
      return; // when typing or with modifiers, let the space through
    }

    // ---------- ENTER -> confirm top result ----------
    // Works even when the search input has focus (parity with main)
    if (key === "Enter" && !this.hasCmdAltCtrl(event)) {
      if (isSearchFocused || this.isGlobalTableOpen()) {
        this._kill(event);
        this.eventHub.emit("globalSearchConfirm");
        return;
      }
    }

    // ---------- VIEW SHORTCUTS (block while typing) ----------
    if (!this.hasCmdAltCtrl(event) && !this.hasShiftOnly(event) && !event.repeat) {
      if (key && key.toLowerCase() === "t") {
        if (typing) return;             // don't hijack while typing
        this._kill(event);
        this.eventHub.emit("viewChange", "top");
        return;
      }
      if (key && key.toLowerCase() === "b") {
        if (typing) return;
        this._kill(event);
        this.eventHub.emit("viewChange", "bottom");
        return;
      }
    }

    // ---------- DELETE ----------
    if (this.shouldHandleDelete(event)) {
      // do not delete while typing in a text field
      if (typing) return;
      this._kill(event);
      this.eventHub.emit("deleteKeyPressed", event);
      return;
    }

    if (key === "Escape") {
      if (this.isGlobalTableOpen() || isSearchFocused) {
        this._kill(event);
        this.eventHub.emit("globalSearchClose"); // listener should hide table + blur input
        return;
      }
      // If nothing to close, let ESC fall through (e.g., other dialogs)
    }

    // Pass everything else through
    this.eventHub.emit("globalKeyDown", event);
  }

  // ---- helpers ----
  _kill(e) {
    try { e.preventDefault(); } catch {}
    try { e.stopPropagation(); } catch {}
    // Some libs attach multiple keydown listeners; ensure none fire after this one.
    if (typeof e.stopImmediatePropagation === "function") {
      try { e.stopImmediatePropagation(); } catch {}
    }
  }

  isSpace(code, key) {
    return code === "Space" || key === " " || key === "Spacebar";
  }

  hasModifier(e) {
    return e.ctrlKey || e.metaKey || e.altKey || e.shiftKey || e.isComposing;
  }

  hasCmdAltCtrl(e) {
    return e.ctrlKey || e.metaKey || e.altKey;
  }

  hasShiftOnly(e) {
    return e.shiftKey && !e.ctrlKey && !e.metaKey && !e.altKey;
  }

  isGlobalTableOpen() {
    const el =
      document.querySelector(".globalTableContainer") ||
      document.getElementById("globalTableContainer") ||
      document.querySelector("#dynamicSearchTable")?.closest("[style]");
    if (!el) return false;
    const cs = window.getComputedStyle(el);
    return cs.display !== "none" && cs.visibility !== "hidden";
  }

  // Only treat *text* inputs as "typing"; buttons/selects/sliders are allowed
  isTypingInTextField(event) {
    const t = event.target;
    if (t instanceof HTMLTextAreaElement) return true;
    if (t && t.isContentEditable) return true;
    if (t instanceof HTMLInputElement) {
      const type = (t.type || "").toLowerCase();
      // textual input types that should block shortcuts
      return ["text","search","email","url","tel","password","number"].includes(type);
    }
    return false;
  }

  isMacPlatform() {
    return (
      navigator.userAgentData?.platform?.startsWith?.("Mac") ||
      navigator.platform.toUpperCase().includes("MAC")
    );
  }

  shouldHandleDelete(event) {
    return (
      (event.key === "Delete" ||
        (event.key === "Backspace" && this.isMacPlatform())) &&
      !this.isTypingInTextField(event)
    );
  }
}

export default KeyboardShortcuts;
